import { useEffect, useMemo, useState } from 'react';
import type { AiAssistantSettings, AiRoleModel, UpdateAiAssistantSettings } from '@/services/aiAssistantSettingsService';

/** A row being edited: the entry plus an id that survives reordering. */
export interface DraftRow extends AiRoleModel {
  id: string;
}

let nextRowId = 0;

/** A new row for the entry, with an id no other row has. */
export const newRow = (entry: AiRoleModel): DraftRow => ({ ...entry, id: `role-model-${nextRowId++}` });

const toRows = (entries: AiRoleModel[] | undefined): DraftRow[] => (entries ?? []).map(newRow);
const toEntries = (rows: DraftRow[]): AiRoleModel[] =>
  rows.map(({ role, provider, model }) => ({ role, provider, model: (model ?? '').trim() }));

const sameEntry = (a: AiRoleModel, b: AiRoleModel) => a.role === b.role && a.provider === b.provider && a.model === b.model;
const sameEntries = (a: AiRoleModel[], b: AiRoleModel[]) => a.length === b.length && a.every((entry, i) => sameEntry(entry, b[i]));

/** Every row names a role and a model, and no role twice: only a role's first row could ever be used. */
function rowsAreSavable(rows: DraftRow[]): boolean {
  const roles = rows.map((r) => (r.role ?? '').toLowerCase());
  return rows.every((r) => r.role && /^\S+$/.test((r.model ?? '').trim())) && new Set(roles).size === roles.length;
}

/** The rows moved: the one at `from` put at `to`, the grid's row reordering. */
export function moveRow<T>(rows: T[], from: number, to: number): T[] {
  const next = [...rows];
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
}

export interface RoleModelsDraft {
  enabled: boolean;
  rows: DraftRow[];
  setEnabled: (enabled: boolean) => void;
  setRows: (rows: DraftRow[]) => void;
  /** A row in the grid is being typed with a changed value — unsaved before it commits. */
  setTyping: (typing: boolean) => void;
  /** The rows added or changed since the load, tinted unsaved. */
  unsavedIds: ReadonlySet<string>;
  dirty: boolean;
  savable: boolean;
  /** Bumped by reset, so the grid is mounted afresh without a row left in edit mode. */
  revision: number;
  reset: () => void;
  /** The update's role fields: none when nothing changed, so a save leaves them as stored. */
  changes: () => Pick<UpdateAiAssistantSettings, 'modelPerRole' | 'roleModels'>;
}

/** The switch and the ordered list as edited on the page, against what is stored. */
export function useRoleModelsDraft(settings: AiAssistantSettings | undefined): RoleModelsDraft {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState<DraftRow[]>([]);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [typing, setTyping] = useState(false);
  const [revision, setRevision] = useState(0);
  const storedEnabled = settings?.modelPerRole ?? false;
  const storedEntries = settings?.roleModels ?? [];

  useEffect(() => {
    const fresh = toRows(settings?.roleModels);
    setEnabled(settings?.modelPerRole ?? false);
    setLoaded(fresh);
    setRows(fresh);
  }, [settings]);

  const unsavedIds = useMemo(() => {
    const byId = new Map(loaded.map((row) => [row.id, row]));
    return new Set(rows.filter((row) => !byId.has(row.id) || !sameEntry(toEntries([row])[0], byId.get(row.id)!)).map((row) => row.id));
  }, [rows, loaded]);

  const dirty = typing || enabled !== storedEnabled || !sameEntries(toEntries(rows), storedEntries);
  return {
    enabled,
    rows,
    setEnabled,
    setRows,
    setTyping,
    unsavedIds,
    dirty,
    savable: rowsAreSavable(rows),
    revision,
    reset: () => {
      setEnabled(storedEnabled);
      setRows(loaded);
      setRevision((n) => n + 1);
    },
    changes: () => (dirty ? { modelPerRole: enabled, roleModels: toEntries(rows) } : {}),
  };
}
